import { useState, useMemo } from 'react';
import { spells } from '../data/spells';
import { SPELL_CLASSES } from '../types';
import type { Spell, SpellClass } from '../types';
import { SpellCard } from '../components/SpellCard';
import { CardListPage, type CardListPageConfig } from '../components/CardListPage';
import { useImport } from '../context/useImport';
import './SpellsPage.css';

type SortOption = 'level' | 'name';

// Wrapper components to adapt entity prop to spell prop
function SpellListCard({ entity, isSelected, onToggle }: { entity: Spell; isSelected: boolean; onToggle: (id: string) => void }) {
  return <SpellCard spell={entity} isSelected={isSelected} onToggle={onToggle} />;
}

export function SpellsPage() {
  const [selectedClass, setSelectedClass] = useState<SpellClass | 'All'>('All');
  const [minLevel, setMinLevel] = useState(0);
  const [maxLevel, setMaxLevel] = useState(9);
  const [sortBy, setSortBy] = useState<SortOption>('level');
  const [importedOnly, setImportedOnly] = useState(false);

  const {
    customSpells,
    customItems,
    customClasses,
    customFeats,
    multiTabImportState,
    importMultiTab,
    importMultipleUrls,
    clearMultiTabImportStatus,
    clearCustomSpells,
    clearCustomItems,
    clearCustomClasses,
    clearCustomFeats,
    combinedTemplateUrl,
  } = useImport();

  // Combine base spells with custom spells
  const allSpells = useMemo(() => [...spells, ...customSpells], [customSpells]);
  const visibleSpells = useMemo(
    () => (importedOnly ? customSpells : allSpells),
    [importedOnly, customSpells, allSpells]
  );

  const filteredSpells = useMemo(() => {
    return visibleSpells
      .filter((spell) => {
        // Class filter
        if (selectedClass !== 'All' && !spell.classes.includes(selectedClass)) {
          return false;
        }
        // Level filter
        if (spell.level < minLevel || spell.level > maxLevel) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'level') {
          if (a.level !== b.level) return a.level - b.level;
          return a.name.localeCompare(b.name);
        }
        return a.name.localeCompare(b.name);
      });
  }, [selectedClass, minLevel, maxLevel, sortBy, visibleSpells]);

  const renderFilters = () => (
    <>
      <div className="filter-row">
        <div className="filter-group">
          <label>CLASS:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value as SpellClass | 'All')}
            className="filter-select"
          >
            <option value="All">All Classes</option>
            {SPELL_CLASSES.map((cls) => (
              <option key={cls} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <label>LEVEL:</label>
          <div className="level-range">
            <input
              type="number"
              min={0}
              max={9}
              value={minLevel}
              onChange={(e) => setMinLevel(Number(e.target.value))}
              className="level-input"
            />
            <span>–</span>
            <input
              type="number"
              min={0}
              max={9}
              value={maxLevel}
              onChange={(e) => setMaxLevel(Number(e.target.value))}
              className="level-input"
            />
          </div>
        </div>

        <div className="filter-group">
          <label>SORT:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="filter-select"
          >
            <option value="level">Level</option>
            <option value="name">A-Z</option>
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group imported-only-group">
          <label htmlFor="spells-imported-only" className="imported-only-label">
            <input
              id="spells-imported-only"
              type="checkbox"
              checked={importedOnly}
              onChange={(e) => setImportedOnly(e.target.checked)}
              className="imported-only-checkbox"
            />
            <span>IMPORTED ONLY</span>
          </label>
        </div>
      </div>
    </>
  );

  const config: CardListPageConfig<Spell> = {
    variant: 'spells',
    pageClassName: 'spells-page',
    listClassName: 'spells-list',
    documentTitle: 'Spell Cards',
    searchPlaceholder: 'Search spells...',
    emptyState: {
      icon: '✨',
      title: 'Spell Cards',
      description: 'Select spells from the list to preview them here',
      instructions: [
        '1. Use the filters to find spells by class or level',
        '2. Click on spells to add them to your selection',
        '3. Download your cards when ready to print',
      ],
    },
    import: {
      onImport: importMultiTab,
      onImportMultiple: importMultipleUrls,
      state: multiTabImportState,
      onClearStatus: clearMultiTabImportStatus,
      spellsCount: customSpells.length,
      itemsCount: customItems.length,
      classesCount: customClasses.length,
      featsCount: customFeats.length,
      onClearSpells: clearCustomSpells,
      onClearItems: clearCustomItems,
      onClearClasses: clearCustomClasses,
      onClearFeats: clearCustomFeats,
      combinedTemplateUrl,
    },
    ListCard: SpellListCard,
    renderFilters,
  };

  return (
    <CardListPage
      allEntities={visibleSpells}
      filteredEntities={filteredSpells}
      config={config}
    />
  );
}
